import { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Card, Button, Divider } from 'antd';
import { ArrowLeftOutlined, FileTextOutlined } from '@ant-design/icons';
import type {
  QuestionnaireSchema,
  QuestionnaireAnswers,
  ValidationErrors,
  QuestionItem,
} from '@/types/questionnaire';
import QuestionItemComponent from './QuestionItem';

export interface QuestionnaireRef {
  // 获取当前答案
  getAnswers: () => QuestionnaireAnswers;
  // 验证所有问题
  validate: () => boolean;
  // 获取验证错误
  getErrors: () => ValidationErrors;
  // 重置表单
  reset: () => void;
  // 提交表单（会触发验证）
  submit: () => Promise<QuestionnaireAnswers | null>;
}

export interface QuestionnaireProps {
  // 问卷元数据（必传）
  schema: QuestionnaireSchema;
  // 答案数据（可选，用于回填）
  answers?: QuestionnaireAnswers;
  // 是否只读模式（可选，默认 false）
  readOnly?: boolean;
  // 取消按钮显示名
  cancelLabel: string;
  // 取消回调（可选）
  onCancel?: () => void;
  // 提交回调（可选）
  onSubmit?: (answers: QuestionnaireAnswers) => void | Promise<void>;
  // 答案变化回调（可选）
  onChange?: (answers: QuestionnaireAnswers) => void;
  // 验证失败回调（可选）
  onValidationError?: (errors: ValidationErrors) => void;
  // 自定义样式类名（可选）
  className?: string;
}

const Questionnaire = forwardRef<QuestionnaireRef, QuestionnaireProps>(
  (
    {
      schema,
      answers: initialAnswers,
      readOnly = false,
      cancelLabel,
      onCancel,
      onSubmit,
      onChange,
      onValidationError,
      className,
    },
    ref,
  ) => {
    const [answers, setAnswers] = useState<QuestionnaireAnswers>(initialAnswers || {});
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 当外部传入的 answers 变化时，更新内部状态
    useEffect(() => {
      if (initialAnswers) {
        setAnswers(initialAnswers);
      }
    }, [initialAnswers]);

    // 验证单个问题
    const validateQuestion = (question: QuestionItem, value: unknown): string | null => {
      const { validation, required: questionRequired } = question;
      const isRequired = validation?.required ?? questionRequired ?? false;

      // 必填验证
      if (isRequired) {
        if (value === null || value === undefined || value === '') {
          return validation?.requiredMessage || `${question.title}为必填项`;
        }
        // 数组类型检查
        if (Array.isArray(value) && value.length === 0) {
          return validation?.requiredMessage || `${question.title}为必填项`;
        }
      }

      // 如果值为空且不是必填，跳过其他验证
      if (value === null || value === undefined || value === '') {
        return null;
      }

      // 正则验证
      if (validation?.pattern && typeof value === 'string') {
        const regex = new RegExp(validation.pattern);
        if (!regex.test(value)) {
          return validation.patternMessage || `${question.title}格式不正确`;
        }
      }

      // 文本域长度验证
      if (question.type === 'textarea' && typeof value === 'string') {
        const config = question.config;
        if ('maxLength' in config && config.maxLength && value.length > config.maxLength) {
          return `${question.title}不能超过 ${config.maxLength} 个字符`;
        }
        if ('minLength' in config && config.minLength && value.length < config.minLength) {
          return `${question.title}不能少于 ${config.minLength} 个字符`;
        }
      }

      return null;
    };

    // 验证所有问题
    const validate = (): boolean => {
      const newErrors: ValidationErrors = {};
      let isValid = true;

      schema.questions.forEach((question) => {
        const value = answers[question.id];
        const error = validateQuestion(question, value);
        if (error) {
          newErrors[question.id] = error;
          isValid = false;
        }
      });

      setErrors(newErrors);
      if (!isValid && onValidationError) {
        onValidationError(newErrors);
      }

      return isValid;
    };

    // 处理答案变化
    const handleAnswerChange = (
      questionId: string,
      value: string | number | string[] | number[] | null | undefined,
    ) => {
      const newAnswers: QuestionnaireAnswers = {
        ...answers,
        [questionId]: value,
      };
      setAnswers(newAnswers);

      // 清除该问题的错误
      if (errors[questionId]) {
        const newErrors = { ...errors };
        delete newErrors[questionId];
        setErrors(newErrors);
      }

      // 触发 onChange 回调
      if (onChange) {
        onChange(newAnswers);
      }
    };

    // 处理提交
    const handleSubmit = async () => {
      if (readOnly) {
        return null;
      }

      // 验证
      if (!validate()) {
        return null;
      }

      setIsSubmitting(true);
      try {
        if (onSubmit) {
          await onSubmit(answers);
        }
        return answers;
      } catch (error) {
        console.error('提交失败:', error);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    };

    // 重置表单
    const reset = () => {
      setAnswers({});
      setErrors({});
    };

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      getAnswers: () => answers,
      validate,
      getErrors: () => errors,
      reset,
      submit: handleSubmit,
    }));

    // 渲染富文本描述
    const renderDescription = (description?: string) => {
      if (!description) return null;

      // 简单的 HTML 渲染（注意：实际项目中应该使用更安全的方案，如 react-markdown）
      // 这里为了简化，直接使用 dangerouslySetInnerHTML
      // 生产环境建议使用 react-markdown 或 DOMPurify 进行安全处理
      return (
        <div
          className="text-slate-700 mb-6 leading-relaxed prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      );
    };

    // 排序问题（按 order 字段）
    const sortedQuestions = [...schema.questions].sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });

    return (
      <Card className={`mt-4 shadow-sm z-[1] ${className || ''}`}>
        <div className="space-y-6">
          {/* 问卷标题 */}
          {schema.title && (
            <div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <FileTextOutlined className="text-indigo-500" />
                <span>{schema.title}</span>
              </h2>
            </div>
          )}

          {/* 问卷描述 */}
          {schema.description && (
            <div>
              <Divider className="my-4" />
              {renderDescription(schema.description)}
            </div>
          )}

          {/* 问题列表 */}
          {sortedQuestions.length > 0 && (
            <div>
              {schema.title && <Divider className="my-4" />}
              <div className="space-y-6">
                {sortedQuestions.map((question) => (
                  <QuestionItemComponent
                    key={question.id}
                    question={question}
                    value={answers[question.id]}
                    error={errors[question.id]}
                    readOnly={readOnly}
                    onChange={(value) => handleAnswerChange(question.id, value)}
                  />
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-center pt-4 gap-4">
            {onCancel && (
              <Button icon={<ArrowLeftOutlined />} onClick={onCancel}>
                {cancelLabel || '返回'}
              </Button>
            )}
            {/* 提交按钮 */}
            {!readOnly && (
              <Button
                type="primary"
                size="large"
                loading={isSubmitting}
                onClick={handleSubmit}
                className="min-w-24"
              >
                提交
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  },
);

Questionnaire.displayName = 'Questionnaire';

export default Questionnaire;
